import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import ProjectCard from '../components/projects/ProjectCard';
import ProjectForm from '../components/projects/ProjectForm';
import { getTemplates, createProjectFromTemplate, deleteProject } from '../utils/api';

const STATUSES = ['all', 'idea', 'planned', 'in-progress', 'completed', 'on-hold'];
const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

const Projects = () => {
  const { projects, fetchProjects, loading } = useApp();
  const { confirm } = useConfirm();
  const { showToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('priority');
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const { data } = await getTemplates();
      setTemplates(data);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleToggleTemplates = () => {
    if (!showTemplates) loadTemplates();
    setShowTemplates((v) => !v);
  };

  const handleUseTemplate = async (tmpl) => {
    const title = window.prompt('Project name:', tmpl.title.replace(' (Template)', ''));
    if (!title) return;
    await createProjectFromTemplate(tmpl._id, { title });
    fetchProjects({ force: true });
  };

  const handleDeleteTemplate = async (tmpl) => {
    if (!(await confirm({ title: 'Delete template', message: `"${tmpl.title}" will be permanently removed.` }))) return;
    try {
      await deleteProject(tmpl._id);
      setTemplates((prev) => prev.filter((t) => t._id !== tmpl._id));
      showToast('Template deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete template', 'error');
    }
  };

  const handleEdit = (project) => { setEditingProject(project); setShowForm(true); };
  const handleClose = () => { setShowForm(false); setEditingProject(null); };

  const filtered = useMemo(() => {
    let list = statusFilter === 'all' ? projects : projects.filter((p) => p.status === statusFilter);
    return [...list].sort((a, b) => {
      if (sort === 'priority') return (PRIORITY_ORDER[a.priority] ?? 2) - (PRIORITY_ORDER[b.priority] ?? 2);
      if (sort === 'target') {
        if (!a.targetDate && !b.targetDate) return 0;
        if (!a.targetDate) return 1;
        if (!b.targetDate) return -1;
        return new Date(a.targetDate) - new Date(b.targetDate);
      }
      if (sort === 'cost') return (b.estimatedCost || 0) - (a.estimatedCost || 0);
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [projects, statusFilter, sort]);

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Projects</h1>
          <p className="page-subtitle">{projects.filter(p => p.status !== 'completed').length} active</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={handleToggleTemplates}>⎘ Templates</button>
          <Link to="/projects/intake" className="btn btn-secondary">✨ AI Intake</Link>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ New Project</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <div className="filter-bar" style={{ margin: 0 }}>
          {STATUSES.map((s) => (
            <button key={s} className={`filter-btn${statusFilter === s ? ' active' : ''}`} onClick={() => setStatusFilter(s)}>
              {s.replace('-', ' ')}
            </button>
          ))}
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          style={{ padding: '0.35rem 0.65rem', borderRadius: 8, border: '1.5px solid var(--border)', fontSize: '0.85rem', fontFamily: 'inherit', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer' }}
        >
          <option value="priority">Sort: Priority</option>
          <option value="target">Sort: Target Date</option>
          <option value="cost">Sort: Cost</option>
          <option value="recent">Sort: Newest</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state-card">
          <p>No projects{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.</p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem' }}>
            <Link to="/projects/intake" className="btn btn-secondary">✨ AI Intake</Link>
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ New Project</button>
          </div>
        </div>
      ) : (
        <div className="card-grid">
          {filtered.map((project) => (
            <ProjectCard key={project._id} project={project} onEdit={handleEdit} />
          ))}
        </div>
      )}

      {showTemplates && (
        <div className="detail-section" style={{ marginTop: '1.5rem' }}>
          <h2 style={{ marginBottom: '0.75rem' }}>⎘ Project Templates</h2>
          {templatesLoading ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Loading…</p>
          ) : templates.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>No templates yet. Open any project and click "Save as Template".</p>
          ) : (
            <div className="card-grid">
              {templates.map((tmpl) => (
                <div key={tmpl._id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ fontWeight: 600 }}>{tmpl.title}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                    {tmpl.category} · {tmpl.materials?.length || 0} materials · {tmpl.steps?.length || 0} steps
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: 'auto' }}>
                    <button className="btn btn-primary btn-sm" onClick={() => handleUseTemplate(tmpl)}>Use Template</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteTemplate(tmpl)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showForm && <ProjectForm existing={editingProject} onClose={handleClose} />}
    </div>
  );
};

export default Projects;
